import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface City {
  id: number;
  nome: string;
  codigoIbge?: number | null;
  estadoId?: number | null;
  isAtivo: boolean;
  estado?: { id: number; nome: string; uf: string } | null;
  ibsMun: number;
}

export interface CityInsert {
  nome: string;
  codigoIbge?: number | null;
  estadoId?: number | null;
  isAtivo?: boolean;
  ibsMun?: number;
}

export interface CitiesParams {
  busca?: string;
  estadoId?: number | null;
  pagina?: number;
  tamanhoPagina?: number;
}

export interface CitiesResponse {
  itens: City[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
  totalPaginas: number;
}

export const useCities = (params: CitiesParams = {}) => {
  const queryClient = useQueryClient();
  const { busca, estadoId, pagina = 1, tamanhoPagina = 100 } = params;

  const query = useQuery({
    queryKey: ["cities", busca, estadoId, pagina, tamanhoPagina],
    queryFn: async () => {
      const { data } = await apiClient.get<CitiesResponse>("/api/cidades", {
        params: {
          ...(busca ? { busca } : {}),
          ...(estadoId ? { estadoId } : {}),
          pagina,
          tamanhoPagina,
        },
      });
      return data;
    },
    placeholderData: (prev) => prev,
  });

  const createMutation = useMutation({
    mutationFn: async (body: CityInsert) => {
      const { data } = await apiClient.post<City>("/api/cidades", body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
      toast.success("Cidade criada com sucesso!");
    },
    onError: (error: Error) => toast.error(`Erro ao criar cidade: ${error.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<CityInsert> }) => {
      const { data: updated } = await apiClient.put<City>(`/api/cidades/${id}`, data);
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
      toast.success("Cidade atualizada com sucesso!");
    },
    onError: (error: Error) => toast.error(`Erro ao atualizar cidade: ${error.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/cidades/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
      toast.success("Cidade excluída com sucesso!");
    },
    onError: (error: Error) => toast.error(`Erro ao excluir cidade: ${error.message}`),
  });

  return {
    data: query.data,
    cities: query.data?.itens ?? [],
    total: query.data?.total ?? 0,
    totalPaginas: query.data?.totalPaginas ?? 1,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    createCity: createMutation.mutate,
    updateCity: updateMutation.mutate,
    deleteCity: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    refetch: query.refetch,
  };
};
